import { memo, useMemo, useState, useCallback, useRef } from 'react';
import { Chip, Popover } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import TemplatesPopover from './TemplatesPopover';
import TemplateFormModal from './TemplateFormModal';
import DeleteModal from 'components/DeleteModal';
import { deleteTemplate } from 'services/MailingService';
import { logEvent } from 'utils/tracking';

import './styles.scss';

const TemplateSelector = ({ onApply }) => {
    const [visible, setVisible] = useState(false);
    const formModal = useRef();
    const deleteModal = useRef();
    const handleFormRef = useCallback((ref) => (formModal.current = ref), []);
    const handleDeleteRef = useCallback((ref) => (deleteModal.current = ref), []);
    const refreshTemplates = useRef();

    const onClick = useCallback((e) => {
        logEvent({
            event: 'emails',
            submodule: 'templates',
            functionality: 'manage',
        });
        setVisible((currentVisible) => !currentVisible);
    }, []);

    const onSaveTemplate = useCallback(
        (template, apply) => {
            refreshTemplates.current?.();
            if (apply) onApply(template.template);
        },
        [onApply],
    );

    const onDeleteTemplate = useCallback((id) => {
        return deleteTemplate(id).then((res) => {
            setVisible(true);
            refreshTemplates.current?.();
            return res;
        });
    }, []);

    const onCancelDelete = useCallback(() => {
        setVisible(true);
    }, []);

    const popoverProps = useMemo(
        () => ({
            className: 'fm-ee__templates-popover',
            placement: 'bottomLeft',
            visible,
            onVisibleChange: setVisible,
            content: (
                <TemplatesPopover
                    onApplyTemplate={onApply}
                    setVisible={setVisible}
                    formModal={formModal}
                    deleteModal={deleteModal}
                    refreshTemplates={refreshTemplates}
                />
            ),
        }),
        [onApply, visible],
    );

    return (
        <>
            <Popover {...popoverProps}>
                <Chip onClick={onClick} icon="emailStar" size="large" isUnfolded>
                    {getLiteral('title_templates')}
                </Chip>
            </Popover>
            <TemplateFormModal
                onRef={handleFormRef}
                setPopoverVisible={setVisible}
                onSave={onSaveTemplate}
            />
            <DeleteModal
                onRef={handleDeleteRef}
                onDelete={onDeleteTemplate}
                onCancel={onCancelDelete}
            />
        </>
    );
};

export default memo(TemplateSelector);
